// Activity-related types that match the backend DTOs and enums exactly

/**
 * BookingStatus represents the overall booking workflow state in the CMS.
 *
 * Automatic Transitions (handled by backend):
 * - Creating booking option → PendingOrganizationApproval
 * - Organization approves → ApprovedAwaitingVendorBooking
 * - Organization rejects/counters → VendorNegotiating
 * - Confirming vendor booking → Confirmed
 *
 * Manual Transitions (Athlyn CMS team):
 * - PendingVendorContact → VendorNegotiating (when team starts working on booking)
 */
export enum BookingStatus {
  PendingVendorContact = "PendingVendorContact",
  VendorNegotiating = "VendorNegotiating",
  PendingOrganizationApproval = "PendingOrganizationApproval",
  ApprovedAwaitingVendorBooking = "ApprovedAwaitingVendorBooking",
  Confirmed = "Confirmed",
  Cancelled = "Cancelled",
  NoVendorsAvailable = "NoVendorsAvailable"
}

// From CMS API BookingOptionStatus enum
export enum BookingOptionStatus {
  Exploring = "Exploring",
  Available = "Available",
  Unavailable = "Unavailable",
  Rejected = "Rejected",
  Approved = "Approved"
}

export enum RevisionCreatedBy {
  Organization = 1,
  Vendor = 2,
  AthlynTeam = 3
}

export enum MessageSender {
  Organization = 1,
  Vendor = 2,
  AthlynTeam = 3,
  System = 4
}

export enum MessageType {
  General = 1,
  RevisionSubmitted = 2,
  Acceptance = 3,
  Rejection = 4,
  Cancellation = 5,
  SystemNotification = 6
}

export enum BookingAction {
  Accept = 1,
  CounterOffer = 2,
  Reject = 3
}

// From Athlyn.Models.Common.ActivityEnums
export enum ActivityType {
  Internal = 1,
  Marketplace = 2
}

export enum ActivityStatus {
  Draft = 1,
  Published = 2,
  InProgress = 3,
  Completed = 4,
  Cancelled = 5
}

export enum RSVPStatus {
  Pending = 1,
  Accepted = 2,
  Declined = 3,
  Cancelled = 4
}

export enum AttendanceStatus {
  NotAttended = 1,
  CheckedIn = 2,
  Attended = 3,
  NoShow = 4
}

export enum CheckInMethod {
  Manual = 1,
  QRCode = 2,
  GPS = 3,
  MobileApp = 4
}

// From Athlyn.Models.Activities.ActivityDto
export interface ActivityDto {
  id: string;
  organizationId: string;
  createdByEmployeeId: string;
  bookingId?: string;
  activityType: ActivityType;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  maxParticipants?: number;
  rsvpEndsBeforeDays: number;
  attendancePoints: number;
  invitedDepartmentIds?: string[];
  invitedLocationIds?: string[];
  invitedEmployeeIds?: string[];
  status: ActivityStatus;
  publishedAt?: string;
  publishedByUserName?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  participants?: ActivityParticipantDto[];
}

// From Athlyn.Models.Activities.ActivityParticipantDto
export interface ActivityParticipantDto {
  id: string;
  activityId: string;
  employeeId: string;
  rsvpStatus: RSVPStatus;
  rsvpDate?: string;
  attendanceStatus: AttendanceStatus;
  checkInDate?: string;
  checkInMethod?: CheckInMethod;
  pointsAwarded?: number;
  createdAt: string;
  updatedAt: string;
}

// From CMS API BookingResponse schema
export interface BookingResponse {
  id: string;
  organizationId: string;
  organizationName?: string;
  organizationLogoUrl?: string;
  activityId: string;
  activityName?: string;
  originalServiceOptionId: string;
  originalVendorName?: string;
  originalServiceName?: string;
  originalServiceOptionName?: string;
  originalMarketplacePrice?: number;
  preferredStartDate: string;
  preferredEndDate: string;
  estimatedParticipantsCount: number;
  specialRequirements?: string;
  status: BookingStatus;
  confirmedStartDate?: string;
  confirmedEndDate?: string;
  confirmedLocation?: string;
  confirmedParticipantsCount?: number;
  confirmedPrice?: number;
  vendorBookingReference?: string;
  vendorBookedAt?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  cancelledByEmployeeName?: string;
  createdAt: string;
  updatedAt: string;
  totalOptionsExplored: number;
  vendorsContacted: number;
  availableOptionsCount: number;
}

// From CMS API BookingSummaryResponse schema
export interface BookingSummaryResponse {
  id: string;
  organizationName?: string;
  activityName?: string;
  originalServiceOptionName?: string;
  originalVendorName?: string;
  preferredStartDate: string;
  estimatedParticipantsCount: number;
  status: BookingStatus;
  totalOptionsExplored: number;
  availableOptionsCount: number;
  vendorsContacted: number;
  daysSinceCreated: number;
  daysUntilPreferredDate: number;
  priority?: string;
  requiresAttention: boolean;
  createdAt: string;
  updatedAt: string;
}

// From CMS API BookingWithOptionsResponse schema
export interface BookingWithOptionsResponse extends BookingResponse {
  bookingOptions?: BookingOptionResponse[];
  suggestedAction?: string;
}

/**
 * BookingOptionResponse represents a single vendor proposal attempt.
 *
 * The Athlyn team creates multiple booking options to track different vendor negotiations.
 * Each option represents one attempt to secure a vendor for the booking.
 *
 * Organization Responses (set by backend):
 * - counterStartDate, counterEndDate, counterCapacity: When organization counters the proposal
 * - rejectionReason: When organization rejects the proposal
 * - respondedByEmployeeName: Name of employee who responded (approve/reject/counter)
 *
 * Note: Organization sees ONLY the latest option (highest optionOrder) with status Available or Approved.
 * Athlyn team sees all options to track negotiation history.
 */
export interface BookingOptionResponse {
  id: string;
  bookingId: string;
  vendorId: string;
  vendorName?: string;
  vendorContactEmail?: string;
  vendorContactPhone?: string;
  serviceId: string;
  serviceName?: string;
  serviceOptionId?: string;
  serviceOptionName?: string;
  marketplacePrice?: number;
  optionOrder: number;
  status: BookingOptionStatus;
  unavailableReason?: string;
  proposedPrice?: number;
  proposedStartDate?: string;
  proposedEndDate?: string;
  proposedLocation?: string;
  proposedCapacity?: number;
  cancellationPolicy?: string;
  specialTerms?: string;
  // Organization response fields (populated by backend)
  counterStartDate?: string;
  counterEndDate?: string;
  counterCapacity?: number;
  rejectionReason?: string;
  respondedByEmployeeName?: string;
  createdAt: string;
  updatedAt: string;
}

// CMS API Request interfaces
export interface CreateBookingOptionRequest {
  serviceOptionId: string;
  contactNotes?: string;
}

export interface UpdateBookingOptionRequest {
  isAvailable: boolean;
  unavailableReason?: string;
  proposedPrice?: number;
  proposedStartDate?: string;
  proposedEndDate?: string;
  proposedLocation?: string;
  proposedCapacity?: number;
  cancellationPolicy?: string;
  specialTerms?: string;
  internalNotes?: string;
}

export interface UpdateBookingStatusRequest {
  status: BookingStatus;
  notes?: string;
}

export interface CancelBookingRequest {
  cancellationReason: string;
  notifyOrganization: boolean;
  internalNotes?: string;
}

export interface ConfirmVendorBookingRequest {
  vendorBookingReference: string;
  vendorBookedAt?: string;
  finalPrice?: number;
  finalLocation?: string;
  finalCapacity?: number;
  confirmationNotes?: string;
}

// Keep the DTO interface for internal data handling
export interface BookingDto extends BookingResponse {}

// UI-friendly interface for CMS Portal (preserves existing component structure)
export interface BookingResponseUI {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogo: string;
  title: string; // Derived from activity name or booking reference
  type: 'marketplace'; // All CMS bookings are marketplace type
  status: 'pending' | 'negotiating' | 'awaiting-approval' | 'vendor-booking' | 'confirmed' | 'cancelled' | 'no-vendors';
  dateSubmitted: string; // createdAt
  description?: string; // specialRequirements or activity description
  currentStep: number; // Workflow step number
  vendorId: string;
  vendorName?: string;
  vendorPackage?: string;
  vendorPrice?: string;
  preferredDatesFormatted: string; // Formatted display of preferred dates
  // Include original booking data for detail views
  booking: BookingResponse;
  activity?: ActivityDto;
  vendor?: {
    name: string;
    package: string;
    price: string;
  };
}

// From Athlyn.Models.Common.FeedbackDto
export interface FeedbackDto {
  id: string;
  organizationId: string;
  entityType: string;
  entityId: string;
  employeeId: string;
  contextId?: string;
  rating: number;
  feedbackText?: string;
  isAnonymous: boolean;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

// API Response types for CMS Portal
export interface ActivityListResponse {
  data: ActivityDto[];
  totalCount: number;
}

export interface BookingListResponse {
  success: boolean;
  data: BookingSummaryResponse[];
  message?: string;
  timestamp: string;
  traceId?: string;
}

export interface BookingDetailResponse {
  success: boolean;
  data: BookingWithOptionsResponse;
  message?: string;
  timestamp: string;
  traceId?: string;
}

export interface BookingOptionsListResponse {
  success: boolean;
  data: BookingOptionResponse[];
  message?: string;
  timestamp: string;
  traceId?: string;
}

export interface FeedbackListResponse {
  data: FeedbackDto[];
  totalCount: number;
}


// Helper functions for CMS Portal booking management
export function mapBookingStatusToUIStatus(status: BookingStatus): string {
  switch (status) {
    case BookingStatus.PendingVendorContact:
      return 'pending';
    case BookingStatus.VendorNegotiating:
      return 'negotiating';
    case BookingStatus.PendingOrganizationApproval:
      return 'awaiting-approval';
    case BookingStatus.ApprovedAwaitingVendorBooking:
      return 'vendor-booking';
    case BookingStatus.Confirmed:
      return 'confirmed';
    case BookingStatus.Cancelled:
      return 'cancelled';
    case BookingStatus.NoVendorsAvailable:
      return 'no-vendors';
    default:
      return 'pending';
  }
}

export function getBookingWorkflowStep(status: BookingStatus): number {
  switch (status) {
    case BookingStatus.PendingVendorContact:
      return 1;
    case BookingStatus.VendorNegotiating:
      return 2;
    case BookingStatus.PendingOrganizationApproval:
      return 3;
    case BookingStatus.ApprovedAwaitingVendorBooking:
      return 4;
    case BookingStatus.Confirmed:
      return 5;
    default:
      return 1;
  }
}

export function formatPreferredDateRange(startDate: string, endDate?: string): string {
  const start = new Date(startDate).toLocaleDateString();
  if (!endDate) return start;
  const end = new Date(endDate).toLocaleDateString();
  return start === end ? start : `${start} - ${end}`;
}

export function getBookingStatusColor(status: BookingStatus): string {
  switch (status) {
    case BookingStatus.PendingVendorContact:
      return 'bg-yellow-100 text-yellow-800';
    case BookingStatus.VendorNegotiating:
      return 'bg-blue-100 text-blue-800';
    case BookingStatus.PendingOrganizationApproval:
      return 'bg-purple-100 text-purple-800';
    case BookingStatus.ApprovedAwaitingVendorBooking:
      return 'bg-indigo-100 text-indigo-800';
    case BookingStatus.Confirmed:
      return 'bg-green-100 text-green-800';
    case BookingStatus.Cancelled:
      return 'bg-red-100 text-red-800';
    case BookingStatus.NoVendorsAvailable:
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getBookingOptionStatusColor(status: BookingOptionStatus): string {
  switch (status) {
    case BookingOptionStatus.Exploring:
      return 'bg-yellow-100 text-yellow-800';
    case BookingOptionStatus.Available:
      return 'bg-green-100 text-green-800';
    case BookingOptionStatus.Unavailable:
      return 'bg-red-100 text-red-800';
    case BookingOptionStatus.Rejected:
      return 'bg-red-100 text-red-800';
    case BookingOptionStatus.Approved:
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function transformBookingToUIDisplay(
  booking: BookingResponse,
  activity?: ActivityDto
): BookingResponseUI {
  const uiStatus = mapBookingStatusToUIStatus(booking.status);

  return {
    id: booking.id,
    organizationId: booking.organizationId,
    organizationName: booking.organizationName || `Organization ${booking.organizationId.slice(0, 8)}`,
    organizationLogo: booking.organizationLogoUrl || "",
    title: booking.activityName || activity?.name || `Booking ${booking.id.slice(0, 8)}`,
    type: 'marketplace',
    status: uiStatus as any,
    dateSubmitted: booking.createdAt,
    description: booking.specialRequirements || activity?.description || undefined,
    currentStep: getBookingWorkflowStep(booking.status),
    vendorId: booking.originalVendorName || 'Unknown Vendor',
    vendorName: booking.originalVendorName || 'Unknown Vendor',
    vendorPackage: booking.originalServiceOptionName || "Standard Package",
    vendorPrice: booking.confirmedPrice ? `AED ${booking.confirmedPrice}` : undefined,
    preferredDatesFormatted: formatPreferredDateRange(booking.preferredStartDate, booking.preferredEndDate),
    booking,
    activity,
    vendor: {
      name: booking.originalVendorName || 'Unknown Vendor',
      package: booking.originalServiceOptionName || "Standard Package",
      price: booking.confirmedPrice ? `AED ${booking.confirmedPrice}` : "Pending"
    }
  };
}